import { DatePicker } from '@mui/lab';
import { Accordion, AccordionDetails, Grid, Typography } from '@mui/material';
import { Fragment } from 'react';
import { useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { ExpandMore } from '@mui/icons-material';
import Table from 'shell/components/shared/table-person';
import { PmiTextField } from 'shell/components/shared/text-field';
import { SubscriptionForeignResidenceStep } from 'subscription/context/aire-subscription';
import AccordionGeneralityResearchSipo from '../shared/generality-research-sipo';
import { PmiButton } from 'shell/components/shared/button';
import StepFamily from './step-family';
import StepResidence from './step-residence';
import StepAttachments from './step-attachments';
import { CommonInterfaceSubscription } from 'subscription/view-models/common-interface-subscription';
import { toast } from 'react-toastify';
import { useAireSubscription } from 'subscription/context/aire-subscription';
import { SubjectData } from 'shell/models/person';

interface SubscriptionForeignResidenceProps {
  validityEqualBirthday?: boolean;
  disabled?: boolean;
  onAvanzaAnagrafe?: (intestatario: unknown) => void;
  onAvanzaFamiglia?: () => void;
  onIndietroFamiglia?: () => void;
  onAvanzaResidenza?: () => void;
  onIndietroResidenza?: () => void;
  onAvanzaAllegati?: () => void;
  onIndietroAllegati?: () => void;
}

const headCellsCittadinanza = [
  { id: 'codice', label: 'Codice' },
  { id: 'cittadinanza', label: 'Cittadinanza' },
];

const SubscriptionForeignResidence = ({
  disabled,
  onAvanzaAnagrafe,
  onAvanzaFamiglia,
  onIndietroFamiglia,
  onAvanzaResidenza,
  onIndietroResidenza,
  onAvanzaAllegati,
  onIndietroAllegati,
}: SubscriptionForeignResidenceProps): React.ReactElement => {
  const formContext = useFormikContext<CommonInterfaceSubscription>();
  const { currentStep, praticaKey } = useAireSubscription();

  const firstCittadinanza = (formContext.values.subjectData as SubjectData)?.cittadinanza?.[0];

  const cittadinanzaTableBody = firstCittadinanza
    ? [
        {
          codice: firstCittadinanza.codiceStato || '',
          cittadinanza: firstCittadinanza.descrizioneStato || '',
        },
      ]
    : [];

  return (
    <Fragment>
      {currentStep === SubscriptionForeignResidenceStep.RegistryOffice ? (
        <>
          <AccordionGeneralityResearchSipo
            disabled={disabled}
            redirectPath="/iscrizioni/residenza/estero/persona-sipo"
          />
          {firstCittadinanza ? (
            <Accordion defaultExpanded>
              <AccordionSummary
                aria-controls="panel-cittadinanza-content"
                id="panel-cittadinanza-header"
                expandIcon={<ExpandMore />}
              >
                <Typography>Cittadinanza</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Table
                      head={headCellsCittadinanza as never}
                      body={cittadinanzaTableBody}
                      ariaLabel="Cittadinanza"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Data validità"
                      aria-label="Data validità cittadinanza"
                      value={formContext.values.citizenshipValidityDate || null}
                      onChange={(date: DateTime | null) => {
                        formContext.setFieldValue('citizenshipValidityDate', date);
                      }}
                      renderInput={(params) => (
                        <PmiTextField
                          {...params}
                          id="citizenshipValidityDate"
                          fullWidth
                          helperText=""
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ) : null}
          <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
            <div className="pmi-w-25 pmi-w-lg-15">
              <PmiButton
                color="cdmBrand"
                variant="outlined"
                id="go-back"
                fullWidth
                disabled={
                  disabled || currentStep === SubscriptionForeignResidenceStep.RegistryOffice
                }
              >
                Indietro
              </PmiButton>
            </div>
            <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                id="forward-operation"
                fullWidth
                onClick={() => {
                  if (!formContext.values.subjectData) {
                    toast.error('Selezionare una persona tramite la ricerca ANPR');
                    return;
                  }
                  if (onAvanzaAnagrafe) {
                    const subjectData = formContext.values.subjectData as SubjectData;
                    const updatedCittadinanza =
                      subjectData?.cittadinanza && subjectData.cittadinanza.length > 0
                        ? subjectData.cittadinanza.map((c, idx) =>
                            idx === 0
                              ? {
                                  ...c,
                                  dataValidita: formContext.values.citizenshipValidityDate
                                    ? formContext.values.citizenshipValidityDate.toISODate()
                                    : c.dataValidita,
                                }
                              : c
                          )
                        : subjectData?.cittadinanza;
                    const preservedIdAnpr = formContext.values.preservedIdSchedaSoggettoAnpr;
                    const currentIdAnpr = subjectData?.generalita?.idSchedaSoggettoANPR;
                    const hasValidCurrentIdAnpr = currentIdAnpr && currentIdAnpr !== '0';
                    const updatedGeneralita = subjectData?.generalita
                      ? {
                          ...subjectData.generalita,
                          idSchedaSoggettoANPR: hasValidCurrentIdAnpr
                            ? currentIdAnpr
                            : preservedIdAnpr,
                        }
                      : subjectData?.generalita;
                    onAvanzaAnagrafe({
                      ...subjectData,
                      generalita: updatedGeneralita,
                      cittadinanza: updatedCittadinanza,
                    });
                  }
                }}
              >
                Avanti
              </PmiButton>
            </div>
          </div>
        </>
      ) : null}
      {currentStep === SubscriptionForeignResidenceStep.Family ? (
        <StepFamily onAvanza={onAvanzaFamiglia} onIndietro={onIndietroFamiglia} />
      ) : null}
      {currentStep === SubscriptionForeignResidenceStep.Reesidence ? (
        <StepResidence
          praticaKey={praticaKey}
          onAvanza={onAvanzaResidenza}
          onIndietro={onIndietroResidenza}
        />
      ) : null}
      {currentStep === SubscriptionForeignResidenceStep.Attachments ? (
        <StepAttachments
          praticaKey={praticaKey}
          onAvanza={onAvanzaAllegati}
          onIndietro={onIndietroAllegati}
        />
      ) : null}
    </Fragment>
  );
};

SubscriptionForeignResidence.displayName = 'Subscription Foreign residence Accordions component';

export default SubscriptionForeignResidence;
